import moment from 'moment-timezone';
import * as Dates from '../Dates';
import * as Units from '../Units';
import * as Weather from '../Weather';

export const extractReportData = (
  data: Record<string, number[]>,
  index: number,
  year: number,
  month: number,
  day?: number,
) => {
  if (typeof day !== 'undefined') {
    // year, month and day are all specified
    if (
      Object.keys(data).includes(
        `${year}/${month.toString().padStart(2, '0')}/${day
          .toString()
          .padStart(2, '0')}`,
      )
    ) {
      return data[
        `${year}/${month.toString().padStart(2, '0')}/${day
          .toString()
          .padStart(2, '0')}`
      ][index];
    }
  } else {
    // only year and month are specified
    if (
      Object.keys(data).includes(`${year}/${month.toString().padStart(2, '0')}`)
    ) {
      return data[`${year}/${month.toString().padStart(2, '0')}`][index];
    }
  }
  return null;
};

export const notNull = (data: Record<string, number[]>, index: number) =>
  Object.entries(data)
    .filter((e) => e[1][index] !== null)
    .reduce(
      (obj, d) => {
        obj[d[0]] = d[1];
        return obj;
      },
      {} as Record<string, number[]>,
    );

export const NotNullTuple = (
  data: Record<string, number[]>,
  index1: number,
  index2: number,
) =>
  Object.entries(data)
    .filter((e) => e[1][index1] !== null && e[1][index2] !== null)
    .reduce(
      (obj, d) => {
        obj[d[0]] = d[1];
        return obj;
      },
      {} as Record<string, number[]>,
    );

export const getMax = (data: Record<string, number[]>, index: number) =>
  Object.keys(notNull(data, index)).length > 0
    ? Math.max(
        ...Object.values(notNull(data, index)).map((values) => values[index]),
      )
    : null;

export const getMin = (data: Record<string, number[]>, index: number) =>
  Object.keys(notNull(data, index)).length > 0
    ? Math.min(
        ...Object.values(notNull(data, index)).map((values) => values[index]),
      )
    : null;

export const getExtremeDate = (
  func: (data: Record<string, number[]>, index: number) => number,
  data: Record<string, number[]>,
  index: number,
  timezone: string,
) => {
  if (Object.keys(data).length > 0) {
    const extremeValue = func(data, index);
    if (extremeValue !== null) {
      // We want to find the latest occurence of extreme values (=> reverse to start from the most recent)
      const extremeDate = Object.keys(data)
        .reverse()
        .find((ts) => data[ts][index] === extremeValue);
      return Dates.formatDate(
        moment.tz(extremeDate, 'YYYY/MM/DD', timezone).unix(),
        timezone,
        'LL',
      );
    }
  }
  return null;
};

export const getMaxDate = (
  data: Record<string, number[]>,
  index: number,
  timezone: string,
) => getExtremeDate(getMax, data, index, timezone);

export const getMinDate = (
  data: Record<string, number[]>,
  index: number,
  timezone: string,
) => getExtremeDate(getMin, data, index, timezone);

export const getSum = (data: Record<string, number[]>, index: number) =>
  Object.keys(notNull(data, index)).length > 0
    ? Object.values(notNull(data, index)).reduce((a, b) => a + b[index], 0)
    : null;

export const getAvg = (data: Record<string, number[]>, index: number) => {
  const notNullData = notNull(data, index);
  if (Object.values(notNullData).length > 0) {
    return getSum(notNullData, index) / Object.keys(notNullData).length;
  }
  return null;
};

export const countLessThan = (
  data: Record<string, number[]>,
  index: number,
  threshold: number,
) =>
  Object.values(notNull(data, index)).filter(
    (values) => values[index] < threshold,
  ).length;

export const countLessThanOrEqual = (
  data: Record<string, number[]>,
  index: number,
  threshold: number,
) =>
  Object.values(notNull(data, index)).filter(
    (values) => values[index] <= threshold,
  ).length;

export const countGreaterThan = (
  data: Record<string, number[]>,
  index: number,
  threshold: number,
) => Object.values(data).filter((values) => values[index] > threshold).length;

export const countGreaterThanOrEqual = (
  data: Record<string, number[]>,
  index: number,
  threshold: number,
) => Object.values(data).filter((values) => values[index] >= threshold).length;

export const countEqual = (
  data: Record<string, number[]>,
  index: number,
  threshold: number,
) => Object.values(data).filter((values) => values[index] === threshold).length;

export const countBetweenValues = (
  data: Record<string, number[]>,
  index: number,
  thresholdLow: number,
  thresholdHigh: number,
) =>
  Object.values(notNull(data, index)).filter(
    (values) => values[index] <= thresholdHigh && values[index] > thresholdLow,
  ).length;

export const getMaxMagnitude = (
  data: Record<string, number[]>,
  units: Units.Units,
  metric: string,
  index1: number,
  index2: number,
) =>
  Math.max(
    ...Object.values(NotNullTuple(data, index1, index2)).map((values) =>
      Math.abs(
        (Units.convertValue(values[index1], metric, units) as number) -
          (Units.convertValue(values[index2], metric, units) as number),
      ),
    ),
  );

export const getMinMagnitude = (
  data: Record<string, number[]>,
  units: Units.Units,
  metric: string,
  index1: number,
  index2: number,
) =>
  Math.min(
    ...Object.values(NotNullTuple(data, index1, index2)).map((values) =>
      Math.abs(
        (Units.convertValue(values[index1], metric, units) as number) -
          (Units.convertValue(values[index2], metric, units) as number),
      ),
    ),
  );

export const getHeatingDegreeDaySum = (
  data: Record<string, number[]>,
  referenceTemperature: number,
  index1: number,
  index2: number,
  costic = false,
) =>
  Weather.sumHeatingDegreeDay(
    Object.values(data).map((values) => [values[index1], values[index2]]),
    referenceTemperature,
    costic,
  );

export const getCoolingDegreeDaySum = (
  data: Record<string, number[]>,
  referenceTemperature: number,
  index1: number,
  index2: number,
  costic = false,
) =>
  Weather.sumCoolingDegreeDay(
    Object.values(data).map((values) => [values[index1], values[index2]]),
    referenceTemperature,
    costic,
  );
